<template>
    <div v-if="showEvents !== null" class="artist-opportunities-container">
        <p class="title">
            {{ showEvents ? 'Upcoming Events' : 'Explore Opportunities' }}
        </p>
        <div class="page-divider"></div>
        <div class="d-flex justify-content-between">
            <span v-if="sortBy.length" class="sort-by mobile-filter-btn"
                >Sort by: {{ sortBy.join(', ') }}</span
            >
            <p v-if="width < 600" v-b-modal.modal-show-filter class="mobile-filter-btn">
                Filter
            </p>
        </div>
        <b-modal
            v-if="width < 600"
            no-enforce-focus
            centered
            hide-footer
            id="modal-show-filter"
            size="l"
            tabindex=""
            content-class="modal-show-filter"
        >
            <template #modal-header>
                <div class="close-modal-filter">
                    <p>Filters</p>
                    <img
                        @click="$bvModal.hide('modal-show-filter')"
                        :src="getIcon() + 'close-event-modal.svg'"
                    />
                </div>
            </template>
            <FilterEvents
                :showEvents="showEvents"
                :authed="authed"
                :showDatepicker.sync="showDatepicker"
                :filterValues.sync="filterValues"
            />
        </b-modal>
        <FilterEvents
            v-if="width > 600"
            :showEvents="showEvents"
            :authed="authed"
            :showDatepicker.sync="showDatepicker"
            :filterValues.sync="filterValues"
            @sort-changed="handleSortChange"
        />
        <div class="page-divider"></div>
        <div v-if="showEvents === false" class="events-container d-flex flex-column">
            <p v-if="!opportunities.length" class="no-event-msg">
                Your search got no hits.  Try removing some of the filters.
            </p>
            <div v-else class="event-cards">
                <base-card
                    v-for="event in filteredEvents"
                    :key="event.id"
                    :image="eventImage(event)"
                    :cta="{
                        text: 'See Details',
                        disabled: false
                    }"
                    :styles="{
                        card: {
                            maxWidth: '336px'
                        }
                    }"
                    @cta-clicked="makeOffer(event.id)"
                >
                    <template #content-main>
                        <div class="opportunity-info">
                            <p class="date">{{ event.start_date }}</p>
                            <p class="venue-name">
                                {{
                                    event.venue_name
                                        ? event.venue_name
                                        : event.venue.venue_name
                                }}
                            </p>
                            <p class="venue-location">
                                {{
                                    event.city_with_state_address.replace(/[0-9]/g, '').trim()
                                }}
                            </p>
                        </div>
                        <p class="vibes">{{ eventVibes(event) }}</p>
                    </template>
                    <template #footer-text>
                        <p class="label">Estimated payout</p>
                        <p
                            v-if="!event.venue_budget || event.venue_budget === '0.00'"
                            class="value"
                        >
                            N/A
                        </p>
                        <p v-else class="value">
                            Up to ${{ parseFloat(event.venue_budget).toFixed(0) }}
                        </p>
                    </template>
                </base-card>
            </div>
            <div class="hedly-fees py-4">*Hedlyner fees not included</div>
        </div>
        <div v-else-if="showEvents" class="events-container w-100">
            <p v-if="!events.length" class="no-event-msg">
                Your search got no hits.  Try removing some of the filters.
            </p>
            <div v-else class="event-cards">
                <base-card
                    v-for="event in filteredEvents"
                    :key="event.id"
                    :image="eventImage(event)"
                    :cta="{
                        text: 'More Details',
                        disabled: false
                    }"
                    :styles="{
                        card: {
                            maxWidth: '336px',
                            maxHeight: '424px'
                        },
                        content: {
                            gap: '40px'
                        }
                    }"
                    @cta-clicked="redirectToEvent(event)"
                >
                    <template #content-main>
                        <div class="event-info">
                            <p class="date">{{ event.start_date }}</p>
                            <p class="venue-name">
                                {{
                                    event.venue_name
                                        ? event.venue_name
                                        : event.venue.venue_name
                                }}
                            </p>
                            <p class="venue-location">
                                {{
                                    event.city_with_state_address.replace(/[0-9]/g, '').trim()
                                }}
                            </p>
                        </div>
                        <div class="event-name-and-artists">
                            <p class="name">{{ event.event_name }}</p>
                            <p class="artists">{{ eventArtists(event) }}</p>
                        </div>
                        <div class="event-start-and-age">
                            <p class="start">
                                {{
                                    `Doors at ${event.start_time
                                        .split(' - ')[0]
                                        .replace(' ', '')}`
                                }}
                            </p>
                            <p v-if="event.min_age" class="age">{{ event.min_age }}+</p>
                            <p v-else class="age">All Ages</p>
                        </div>
                    </template>
                    <template #footer-text>
                        <p
                            v-if="event.cover_cost && event.cover_cost !== '0.00'"
                            class="value"
                        >
                            {{ `$${parseFloat(event.cover_cost).toFixed(0)} Cover` }}
                        </p>
                    </template>
                </base-card>
            </div>
        </div>
        <div v-if="showDatepicker && width > 600" class="date-picker-wrapper">
            <Datepicker
                :pickedDates="filterValues.dates"
                :mode.sync="datepickerMode"
                @saveDates="handleSaveDates"
                @closeDatepicker="handleCloseDatepicker"
            />
        </div>
        <div v-else-if="showDatepicker && width < 600" class="date-picker-mobile-wrapper">
            <DatepickerMobile
                :pickedDates="filterValues.dates"
                :mode.sync="datepickerMode"
                @saveDates="handleSaveDates"
                @closeDatepicker="handleCloseDatepicker"
            />
        </div>
        <ArtistDirectBooking
            v-if="eventId"
            :authed="authed"
            :event-id="eventId"
            :user-id="userData.id"
            :step-id="bookingModalStep"
            :artist-pages="artistPages"
            :show-modal.sync="showBookingModal"
        />
    </div>
</template>
<script>
    import axios from 'axios'
    import ArtistDirectBooking from '../../artist-direct-booking/ArtistDirectBooking.vue'
    import Datepicker from './Datepicker.vue'
    import DatepickerMobile from './DatepickerMobile.vue'
    import { MONTHS } from '@/constants/calendar'
    import sortEventFunction from '@/mixins/sort-events/sort-by-date'
    import checkScreen from '@/mixins/check-screen'
    import FilterEvents from './parts/FilterEvents.vue'
    import { useAuth } from '@/composables/auth/useAuth'
    import { useLocalStorage } from '@/composables/utils/useLocalStorage'
    import { useArtistDirectBookingInternal } from '@/composables/features/artist-booking/direct-booking/useArtistDirectBooking'
    import BaseCard from '@/components/ui/cards/BaseCard.vue'
    import moment from 'moment'

    const URLS = {
        xhr: {
            opportunitiesGuest: '/api/events/new-and-proceed-list-guest',
            opportunities: '/api/events/new-and-proceed-list',
            upcomingEvents: '/api/events/confirmed-list-guest',
            upcomingEventsAuth: '/api/events/confirmed-and-completed-list',
            checkAuth: '/check-authorization',
            user: '/profile/v1/settings/user',
            artistContact: '/profile/contacts/artist/list'
        },
        images: {
            defaultEventImage: '/images/default-artist-event.png'
        }
    }

    const SORT_BY = {
        dates: 'Dates',
        distance: 'Distance',
        budget: 'Budget',
        minDuration: 'Minimum Duration',
        maxDuration: 'Maximum Duration',
        vibe: 'Relevancy',
        follow: 'Contacts'
    }

    export default {
        name: 'ArtistOpportunities',

        components: {
            Datepicker,
            DatepickerMobile,
            FilterEvents,
            ArtistDirectBooking,
            BaseCard
        },
        mixins: [sortEventFunction, checkScreen],
        props: {
            showDatepicker: {
                type: Boolean
            },
            filterValues: {
                type: Object,
                default: () => ({ dates: [] })
            }
        },
        setup() {
            const { social } = useArtistDirectBookingInternal()
            const { localGet } = useLocalStorage()
            return {
                social,
                localGet
            }
        },
        data: () => ({
            eventId: null,

            showBookingModal: false,

            bookingModalStep: null,

            authed: false,

            artistPages: [],

            contacts: [],

            opportunities: [],

            events: [],

            sortBy: [],

            userData: {},

            showEvents: null,

            datepickerMode: 'range',

            sortType: ''
        }),
        methods: {
            handleSortChange(selectedSort) {
                this.sortType = selectedSort
            },
            countRelevancyStrings(array, string) {
                if (array.length && string.length) {
                    let count = 0
                    const artistKeywords = string.toLowerCase().split(',')
                    for (let i = 0; i < array.length; i++) {
                        if (artistKeywords.includes(array[i].toString().toLowerCase())) {
                            count++
                        }
                    }
                    return count
                }
            },
            distanceBetweenUserAndEvent(lat1, lon1, lat2, lon2) {
                const toRadians = (deg) => (deg * Math.PI) / 180.0
                const earthRadiusMiles = 3958.8
                const dLat = toRadians(lat2 - lat1)
                const dLon = toRadians(lon2 - lon1)
                const radiusCalculation =
                    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(toRadians(lat1)) *
                        Math.cos(toRadians(lat2)) *
                        Math.sin(dLon / 2) *
                        Math.sin(dLon / 2)
                const calculateDistance =
                    2 *
                    Math.atan2(Math.sqrt(radiusCalculation), Math.sqrt(1 - radiusCalculation))
                return earthRadiusMiles * calculateDistance
            },
            sortEvents(filteredEvents, sortType) {
                switch (sortType) {
                    case 'Relevancy':
                        return filteredEvents.sort((a, b) => {
                            const countA = this.countRelevancyStrings(
                                a.array_keywords,
                                this.artistPages.map((artist) => artist.keyword).join(',')
                            )
                            const countB = this.countRelevancyStrings(
                                b.array_keywords,
                                this.artistPages.map((artist) => artist.keyword).join(',')
                            )
                            return countB - countA
                        })
                    case 'Distance':
                        return filteredEvents.sort((a, b) => {
                            const distanceA = this.distanceBetweenUserAndEvent(
                                a.latitude,
                                a.longitude,
                                this.userData.lat,
                                this.userData.lng
                            )
                            const distanceB = this.distanceBetweenUserAndEvent(
                                b.latitude,
                                b.longitude,
                                this.userData.lat,
                                this.userData.lng
                            )
                            return distanceA - distanceB
                        })
                    case 'Newest':
                        return filteredEvents.sort(
                            (a, b) =>
                                moment(a.start_date, 'MMMM DD, YYYY') -
                                moment(b.start_date, 'MMMM DD, YYYY')
                        )
                    case 'Budget':
                        return filteredEvents.sort((a, b) => b.venue_budget - a.venue_budget)
                    default:
                        return filteredEvents
                }
            },
            getDoorsAt(startTime) {
                return startTime.split(' - ')[0]
            },
            getIcon() {
                return '/images/'
            },
            checkDefaultImage(url) {
                return url.includes('default.png')
            },

            handleCloseDatepicker() {
                this.showDatepicker = false
            },

            handleSaveDates(data) {
                this.filterValues.dates = data

                this.filterValues.dateInput = data
                    .map((d) => {
                        const month = d.toLocaleString('default', { month: 'short' })
                        const day = d.getDate()

                        return `${month} ${day}`
                    })
                    .join(', ')

                this.handleCloseDatepicker()
            },

            filterByDates(events) {
                let self = this

                return events.filter((e) => {
                    let match = true
                    let startDate = e.start_date.split(' ').map((v) => v.replace(',', ''))

                    let startDay = startDate[1]
                    let startMonth = startDate[0]
                    let startYear = startDate[2]
                    let filterDates = self.filterValues.dates

                    for (let i = 0; i < filterDates.length; i++) {
                        let date = filterDates[i]
                        match = true

                        if (startYear !== date.getFullYear().toString()) {
                            match = false
                        }
                        if (startMonth !== MONTHS[date.getMonth()]) {
                            match = false
                        }
                        if (startDay !== date.getDate().toString()) {
                            match = false
                        }

                        if (match) return match
                    }
                })
            },

            filterByDistance(events) {
                let self = this

                return events.filter((e) => {
                    let centerPt = { lat: e.latitude, lon: e.longitude }
                    let checkPt = { lat: self.filterValues.lat, lon: self.filterValues.lon }
                    let radius = self.filterValues.distance

                    // for approx accuracy, assume flat earth
                    // circumference of earth in miles
                    let ky = 24_900 / 360
                    let kx = Math.cos((Math.PI * centerPt.lat) / 180) * ky
                    let dx = Math.abs(centerPt.lon - checkPt.lon) * kx
                    let dy = Math.abs(centerPt.lat - checkPt.lat) * ky

                    return Math.sqrt(dx * dx + dy * dy) <= radius
                })
            },

            filterByVibe(events) {
                if (!this.authed) return events

                let vibes

                if (this.artistPages.some((artist) => artist.keyword)) {
                    vibes = this.artistPages.map((artist) => artist.keyword).join(',')
                } else {
                    vibes = []
                }
                return events.filter((e) =>
                    e.array_keywords.some((keyword) => vibes.includes(keyword))
                )
            },

            filterByFollow(events) {
                if (!this.authed) return events

                let venueContacts = this.contacts.map((c) => c.venue_id)

                return events.filter((e) => venueContacts.includes(e.venue.id))
            },

            pushSortBy(key) {
                let target = SORT_BY[key]

                let indx = this.sortBy.indexOf(target)

                if (indx === -1) {
                    this.sortBy.push(target)
                }
            },

            clearSortBy(key) {
                let target = SORT_BY[key]
                let indx = this.sortBy.indexOf(target)

                if (indx >= 0) {
                    this.sortBy.splice(indx, 1)
                }
            },

            async checkAuth() {
                try {
                    const resp = await axios.get(this.URLS.xhr.checkAuth)

                    if (resp?.data?.isAuthorized) {
                        this.authed = true
                        return true
                    }
                    return false
                } catch {
                    this.authed = false
                    return false
                }
            },

            async initUserData() {
                let userResp = await axios.get(this.URLS.xhr.user)
                let userId = userResp.data.data[0].id
                this.userData = userResp.data.data[0]
                this.artistPages = userResp.data.data[0].artistPages

                let contactResp = await axios.get(this.URLS.xhr.artistContact)
                this.contacts = contactResp.data.data
            },
            makeOffer(eventId) {
                this.eventId = eventId
                this.showBookingModal = true
            },
            redirectToEvent(event) {
                window.open(`/event/show/${event.id}`, '_blank')
            },

            launchOpportunityIfRequested() {
                let params

                try {
                    params = new URL(window.location.toString()).searchParams
                } catch {
                    return
                }

                const openOpportunity = JSON.parse(params.get('opportunity'))

                if (
                    openOpportunity &&
                    this.opportunities &&
                    this.opportunities.find((op) => op.id === openOpportunity)
                ) {
                    this.eventId = openOpportunity
                    this.showBookingModal = true
                }
            }
        },

        computed: {
            URLS: () => URLS,

            eventArtists: () => (event) => {
                let artists = ''

                if (event.bookedArtists.length > 3) {
                    artists = event.bookedArtists
                        .slice(0, 3)
                        .map((a) => a.band_name)
                        .join(', ')

                    artists = `With ${artists} and ${event.bookedArtists.length - 3} more...`
                } else {
                    artists = event.bookedArtists.map((a) => a.band_name).join(', ')

                    artists = `With ${artists}`
                }

                return artists
            },
            eventTags: () => (event) => event.array_keywords.filter((k) => k),

            eventVibes: () => (event) => {
                let keywords

                if (event.array_keywords.length > 3) {
                    keywords = event.array_keywords.slice(0, 3).join(', ')

                    keywords = `${keywords} and ${event.array_keywords.length - 3} more...`
                } else {
                    keywords = event.array_keywords.join(', ')
                }

                return keywords
            },

            filteredEvents: (self) => {
                let filteredEvents = self.opportunities.length
                    ? self.opportunities
                    : self.events

                if (self.filterValues.dates.length) {
                    self.pushSortBy('dates')
                    filteredEvents = self.filterByDates(filteredEvents)
                } else {
                    self.clearSortBy('dates')
                }

                if (
                    self.filterValues.location &&
                    self.filterValues.lat &&
                    self.filterValues.lon &&
                    self.filterValues.distance
                ) {
                    self.pushSortBy('distance')
                    filteredEvents = self.filterByDistance(filteredEvents)
                } else {
                    self.clearSortBy('distance')
                }

                if (self.filterValues.minBudget) {
                    self.pushSortBy('budget')
                    filteredEvents = filteredEvents.filter(
                        (e) => e.budget >= parseInt(self.filterValues.minBudget)
                    )
                } else {
                    self.clearSortBy('budget')
                }

                if (self.filterValues.minDuration) {
                    self.pushSortBy('minDuration')
                    filteredEvents = filteredEvents.filter(
                        (e) => e.duration >= parseInt(self.filterValues.minDuration)
                    )
                } else {
                    self.clearSortBy('minDuration')
                }

                if (self.filterValues.maxDuration) {
                    self.pushSortBy('maxDuration')
                    filteredEvents = filteredEvents.filter(
                        (e) => e.duration <= parseInt(self.filterValues.maxDuration)
                    )
                } else {
                    self.clearSortBy('maxDuration')
                }

                if (self.filterValues.matchVibe) {
                    self.pushSortBy('vibe')
                    filteredEvents = self.filterByVibe(filteredEvents)
                } else {
                    self.clearSortBy('vibe')
                }

                if (self.filterValues.matchFollow) {
                    self.pushSortBy('follow')
                    filteredEvents = self.filterByFollow(filteredEvents)
                } else {
                    self.clearSortBy('follow')
                }

                return self.sortEvents(filteredEvents, self.sortType)
            },

            showDefaultOpportunityImage: () => (event) => !event.image && !event.venue?.avatar,

            eventImage: (self) => (event) => {
                if (event.image && !event.image.includes('/global/default.png')) {
                    return event.image
                } else if (event.venue?.avatar?.includes('/global/default.png')) {
                    return self.URLS.images.defaultEventImage
                } else {
                    return event.venue?.avatar
                }
            }
        },

        async created() {
            try {
                if (await this.checkAuth()) {
                    await this.initUserData()
                }
            } catch {}

            const currentUrl = window.location.href
            if (currentUrl.split('/').pop() === 'events') {
                this.showEvents = true
                let resp = await axios.get(
                    this.authed
                        ? this.URLS.xhr.upcomingEventsAuth
                        : this.URLS.xhr.upcomingEvents
                )
                this.events = this.sortByDate(this.authed ? resp.data.data : resp.data.bids)
            } else {
                this.showEvents = false
                let resp = await axios.get(
                    this.authed
                        ? this.URLS.xhr.opportunities
                        : this.URLS.xhr.opportunitiesGuest
                )
                this.opportunities = this.sortByDate(resp.data.bids)
            }

            this.launchOpportunityIfRequested()
        },
        mounted() {
            const bookingSteps = this.localGet('direct-booking-modal', {
                remove: true,
                fallback: false
            })
            const bookingStep = this.localGet('direct-booking-step', {
                remove: true,
                fallback: null
            })
            const eventId = this.localGet('direct-booking-event', {
                remove: true,
                fallback: null
            })
            if (bookingStep && bookingSteps) {
                this.showBookingModal = bookingSteps
                this.bookingModalStep = bookingStep
                this.eventId = eventId
                this.social = true
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .modal-show-filter {
        min-width: 350px;
        border-radius: 15px;
        border: 1px solid rgba(255, 255, 255, 0.14);
        background: #262626;
        position: relative;
    }
</style>

<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    $laptop: 1300px;

    p {
        margin-bottom: 0px;
    }
    @media screen and (min-width: $laptop) {
        .date-picker-wrapper {
            transform: translate(-50%);
            left: 50%;
        }
    }
    .event-details {
        display: flex;
        gap: 18px;
    }
    .event-details p {
        color: #acacac;
        @include base-font-14;
        font-weight: 700;
        line-height: 130%;
        letter-spacing: 0.14px;
    }
    .event-time {
        color: #acacac;
        @include base-font-20;
        font-weight: 550;
        line-height: 120%;
        letter-spacing: -0.2px;
    }
    .event-title {
        color: #dedede;
        @include base-font-26;
        line-height: 120%;
        letter-spacing: -0.52px;
    }
    .responded-artists {
        display: flex;
        padding: 5px 12px 5px 5px;
        align-items: center;
        gap: 10px;
        border-radius: 100px;
        border: 1px solid #acacac;
        background: #262626;
    }
    .responded-artists img {
        max-width: 28px;
        min-height: 28px;
        border-radius: 50%;
    }
    .event-with {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-bottom: 10px;
    }
    .event-text {
        color: #acacac;
        @include base-font-14;
        font-weight: 700;
        line-height: 130%;
        letter-spacing: 0.14px;
        margin: 0;
        text-decoration: none;
    }
    .event-content {
        display: flex;
        padding: 20px;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        flex: 0 1 80%;
        border-radius: 0 15px 15px 0;
        background: #262626;
    }
    .event-image img {
        max-height: 280px;
        height: 100%;
        width: 320px;
        object-fit: cover;
        border-radius: 15px 0 0 15px;
    }
    .event-vide {
        display: flex;
        align-items: flex-start;
        gap: 2px;
    }
    .event-header {
        display: flex;
        align-items: baseline;
        gap: 6px;
    }
    .event-header-wrapper {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .venue-full-name {
        color: #acacac;
        @include base-font-26;
        line-height: 120%;
        letter-spacing: -0.52px;
    }
    .event-end-date {
        text-wrap: nowrap;
        color: #dedede;
        @include base-font-38;
        line-height: 110%;
        letter-spacing: -0.76px;
    }
    .events-card {
        display: flex;
        width: 100%;
        height: 237px;
        cursor: pointer;
    }
    .event-image {
        width: 100%;
        flex: 0 1 20%;
        border-radius: 15px 0 0 15px;
        opacity: 0.8;
    }
    .default-text {
        color: #646464;
        @include base-font-20;
        font-weight: 550;
        line-height: 120%;
        letter-spacing: -0.2px;
    }
    .default-logo-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 16px;
        min-width: 336px;
        min-height: 250px;
        opacity: 0.8;
        background: #acacac;
        border-radius: 15px 15px 0 0;
    }
    .default-logo-container img {
        max-width: 66px;
        max-height: 72px;
    }
    .artist-opportunities-container {
        max-width: 1170px;
        min-height: 2000px;
        display: flex;
        flex-direction: column;
        padding: 10px 60px;
        background: #121212;
        margin-left: auto;
        margin-right: auto;

        /* Chrome, Safari, Edge, Opera */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Firefox */
        input[type='number'] {
            -moz-appearance: textfield;
        }
        .title {
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 44px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
        }

        .page-divider {
            width: 100%;
            height: 1px;
            background: #646464;
            margin-top: 27px;
            margin-bottom: 20px;
        }

        .events-container {
            .no-event-msg {
                color: #acacac;
                font-family: Instrument Sans;
                font-size: 26px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.52px;
            }

            .event-cards {
                display: flex;
                flex-direction: row;
                gap: 20px;
                flex-wrap: wrap;

                .event-name-and-artists {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: flex-start;

                    .name {
                        overflow: hidden;
                        color: #8b8b8b;
                        font-variant-numeric: ordinal;
                        text-overflow: ellipsis;
                        @include base-font-14;
                        font-weight: 600;
                    }
                    .artists {
                        color: #8b8b8b;
                        font-variant-numeric: ordinal;
                        @include base-font-12;
                        font-weight: 400;
                        text-wrap: wrap;
                    }
                }
                .event-start-and-age {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 16px;

                    .start,
                    .age {
                        color: #8b8b8b;
                        @include base-font-14;
                    }
                }
                .event-info,
                .opportunity-info {
                    .date {
                        color: #dedede;
                        font-variant-numeric: ordinal;
                        @include base-font-20;
                        font-weight: 600;
                        line-height: 24px;
                    }
                    .venue-name,
                    .venue-location {
                        color: #dedede;
                        font-variant-numeric: ordinal;
                        @include base-font-12;
                    }
                }
                .vibes {
                    overflow: hidden;
                    color: #8b8b8b;
                    font-variant-numeric: ordinal;
                    text-overflow: ellipsis;
                    @include base-font-12;
                    font-weight: 400;
                }
                .label {
                    color: #8b8b8b;
                    font-variant-numeric: ordinal;
                    @include base-font-12;
                    font-weight: 400;
                }
                .value {
                    color: #acacac;
                    font-variant-numeric: ordinal;
                    @include base-font-16;
                    line-height: 24px;
                }

                .event-card {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    max-width: fit-content;
                    width: 100%;
                    min-height: 513px;
                    border-radius: 15px;
                    background: #262626;

                    .card-top {
                        .venue-img {
                            min-height: 250px;
                            img {
                                border-radius: 15px 15px 0px 0px;
                                width: 336px;
                                height: 250px;
                                object-fit: cover;
                            }
                        }
                    }

                    .card-bottom {
                        display: flex;
                        flex-direction: column;
                        padding: 15px 20px 20px 20px;

                        .event-date {
                            overflow: hidden;
                            color: #acacac;
                            text-overflow: ellipsis;
                            font-family: Instrument Sans;
                            font-size: 26px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 120%;
                            letter-spacing: -0.52px;
                        }

                        .venue-name {
                            margin-top: 5px;
                            color: #acacac;
                            font-family: Instrument Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 550;
                            line-height: 125%;
                            max-width: 296px;
                        }

                        .venue-location {
                            margin-top: 5px;
                            color: #646464;
                            font-family: Instrument Sans;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 600;
                            line-height: 130%;
                            letter-spacing: 0.14px;
                        }

                        .event-tags {
                            margin-top: 5px;
                            display: flex;
                            flex-direction: row;
                            gap: 5px;
                            max-height: 40px;
                            max-width: 295px;
                            overflow-y: scroll;
                            scrollbar-width: none;

                            .event-tag {
                                border-radius: 100px;
                                border: 1px solid #8b8b8b;
                                min-width: max-content;
                                padding: 3px 10px 4px 10px;
                                color: #8b8b8b;
                                font-family: Instrument Sans;
                                font-size: 12px;
                                font-style: normal;
                                font-weight: 650;
                                line-height: 135%;
                                letter-spacing: 0.12px;
                            }
                        }

                        .event-tags::-webkit-scrollbar {
                            width: 0;
                        }

                        .page-divider {
                            margin-top: 15px !important;
                            margin-bottom: 15px !important;
                        }

                        .event-cost {
                            color: #c9cd00;
                            font-family: Instrument Sans;
                            font-size: 22px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 120%;
                            letter-spacing: -0.22px;
                            max-width: 296px;
                        }

                        button {
                            margin-top: 15px;
                            max-width: fit-content;
                            border-radius: 120px;
                            background: #c9cd00;
                            border: none;
                            padding: 12px 32px;
                            color: #000;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 550;
                            line-height: 120%;
                            border: 0;
                        }

                        button:hover {
                            background: #fff;
                        }
                    }
                }
            }
        }
        .hedly-fees {
            color: #646464;
            @include base-font-12;
            font-weight: 650;
            line-height: 135%;
            letter-spacing: 0.12px;
        }

        .date-picker-wrapper {
            flex: none;
            position: absolute;
            background: #262626;
            width: 1180px;
            min-height: 763px;
            border-radius: 15px;
            top: 25%;
            z-index: 11;
        }
        .date-picker-mobile-wrapper {
            position: fixed;
            left: 0;
            top: 0;
            width: 100vw;
            height: 100vh;
            background: black;
            display: flex;
            flex-direction: row;
            justify-content: center;
            overflow-y: scroll;
        }
    }

    .mobile-filter-btn {
        color: #dedede;
        text-align: right;
        @include base-font-18;
        font-weight: 650;
        line-height: 120%;
        margin: 0;
    }

    .close-modal-filter {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .close-modal-filter p {
        color: #dedede;
        @include base-font-22;
        line-height: 120%;
        letter-spacing: -0.22px;
        margin: 0;
    }

    @media (max-width: 600px) {
        .event-cards {
            justify-content: center;
        }
        .artist-opportunities-container {
            padding: 0;
        }
        .default-logo-container {
            min-width: 330px;
        }
        .artist-opportunities-container .events-container .event-cards .event-card {
            max-width: none;
        }
        .events-card {
            flex-direction: column;
            max-width: 425px;
            height: 100%;
        }
        .event-content {
            border-radius: 0 0 15px 15px;
            gap: 6px;
        }
        .event-image {
            border-radius: 15px 15px 0 0;
            display: flex;
            justify-content: center;

            img {
                width: 100% !important;
                height: 247px !important;
                border-radius: 15px 15px 0 0;
            }
        }
        .event-header {
            flex-direction: column;
        }

        .event-end-date {
            margin: 0;
            font-size: 22px;
        }
        .venue-full-name {
            font-size: 20px;
            margin: 0;
        }
        .event-with {
            flex-wrap: wrap;
        }
        .event-title {
            margin: 0;
        }
        .event-details p {
            margin: 0;
        }
    }
</style>
